/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { t } from '@web-solutions/module-localization';
import Analytics from '@web-solutions/module-analytics';
import { PaymentSystem } from '@web-solutions/react-billing';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { useUpsalePurchase } from 'core/upsale/hooks/use-upsale-purchase';
import ErrorPopup from 'core/payment/components/error-popup';
import { extractDeclineCode } from 'core/payment/utils/get-payment-error-content';
import {
  PaymentErrorState,
  getPaymentErrorAnalyticsParams,
  resolvePaymentErrorState,
} from 'core/payment/utils/payment-error-state';
import {
  createOneClickPayment,
  handleErrorPurchase,
  init as initBilling,
} from 'core/store/billing/actions';
import { selectPaymentProject, selectPaymentSystem } from 'core/store/billing/selectors';
// @ts-ignore — untyped JS module
import Billing from '@web-solutions/module-billing';
import { getPublicKeys } from '@web-solutions/react-billing';
import { confirmStripePaymentIntent } from 'core/utils/stripe-payment';
import { getLocalizedList, getLocalizedText } from 'core/utils/localized';
import getLocale from 'src/localization/get-locale';
import { useNav } from 'src/onboarding/hooks/nav';
import { useStore } from 'src/onboarding/store/store';
import { applyFoodzeeAuthTokens } from 'src/onboarding/helpers/applyFoodzeeAuthTokens';
import { useFrontPhoto } from '../_shared/useFrontPhoto';
import { img, trackFromGender } from '../_shared/hairstyle';
import styles from './style.module.scss';

// Analytics category of the offer's failed-payment sheet, kept apart from the
// paywall's own error popup so upsale failures are countable on their own.
const SHEET_CATEGORY = 'payment_failed_sheet';

// "$19.99" / "19.99" -> 19.99
const parsePriceAmount = (value: any): number | undefined => {
  const parsed = Number(String(value ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const formatUsd = (amount: number) => `$${amount.toFixed(2)}`;

// Hairstyle-only defaults for the fields the shared upsaleDynamic default
// (packages/core, an AI DNA report) does not carry. A manager edits them in the
// mutator; an unset field falls back to what is here.
const DEFAULTS = {
  discountPercent: 50,
  buttonText: 'Get Report',
  whatYouGet: [
    'Density, thickness & scalp report',
    'Barber-ready cut instructions',
    'Daily routine & product picks',
    'Clear AI-powered explanations',
  ],
  offerBadgeText: 'One-time offer',
  oneTimePaymentText: 'One time payment',
};

// A boolean switch from the mutator: undefined / '' keeps the default (on),
// only an explicit false / "false" turns the element off.
const isOn = (value: any) => !(value === false || value === 'false');

// Round gradient check in front of every "What you get" line (design 02.5).
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 13l4.5 4.5L19 7" />
  </svg>
);

function Page() {
  const { currentIndex, onNext } = useNav();
  const { upsaleDynamic, showUpsalePaymentError } = useRemoteConfig() as any;
  const locale = getLocale();

  const gender = useStore((s: any) => s.hairstyleGender);
  const front = useFrontPhoto();
  const setPage = useStore((s: any) => s.setPage);

  const dispatch = useDispatch<any>();
  const paymentSystem = useSelector(selectPaymentSystem);
  const paymentProject = useSelector(selectPaymentProject);
  const purchaseState = useSelector((s: any) => s.billing.purchase);
  const isStripe = paymentSystem === PaymentSystem.STRIPE;

  const apiAuthSource = useSelector((s: any) => s.profile?.apiAuth?.source);
  const authTokens = useStore((s: any) => s.authTokens);
  const userId = useStore((s: any) => s.userId);

  // Reload survival. The Billing singleton lives in memory: after a reload it
  // has no payment system / project / API host (the charge then goes to the
  // generic billing API instead of the Foodzee one and is rejected) and no
  // Foodzee bearer. The redux profile, however, IS persisted with
  // apiAuth.source === 'foodzee', so "source is already foodzee" says nothing
  // about the module — only the module's own state does. `authorized` is
  // blacklisted from persistence, so an "authorized" flag cannot be relied
  // on either. The paywall does this set-up on arrival; this
  // screen has to as well, or a purchase only works when the user came
  // straight from the paywall in the same session.
  //
  // No network here: the paywall already fetched the Stripe public key in
  // this session, and a server-side charge needs only the payment system,
  // project and API host — all known from the mutator. Billing.restore() puts
  // exactly those back. The key is fetched lazily (ensureStripeKeys) only if
  // 3-D Secure or the card form actually needs Stripe.js.
  useEffect(() => {
    if (!isStripe || !authTokens?.accessToken) {
      return;
    }

    if (Billing.paymentSystemType !== paymentSystem) {
      Billing.restore(paymentSystem, paymentProject);
    }

    if (apiAuthSource !== 'foodzee' || !Billing.apiAuth) {
      applyFoodzeeAuthTokens(
        {
          accessToken: authTokens.accessToken,
          refreshToken: authTokens.refreshToken,
          uid: authTokens.userId || userId,
        },
        dispatch,
        { reinitBilling: false, paymentSystem }
      );
    }
  }, [apiAuthSource, authTokens, userId, dispatch, isStripe, paymentSystem, paymentProject]);

  /**
   * Stripe.js needs the public key, which lives only in memory and is gone
   * after a reload. Fetch it (one /payments/config round-trip) only at the
   * moment something client-side needs it: 3-D Secure or the card form.
   */
  const ensureStripeKeys = async () => {
    if (getPublicKeys()?.stripe?.public_key) {
      return;
    }

    const ok = await dispatch(initBilling());

    if (ok === false) {
      // Auth not synced into the store yet — talk to the module directly.
      await Billing.init(paymentSystem, paymentProject);
    }
  };

  const {
    handleUpsalePurchase,
    renderPaymentProcessor,
    showPaymentPopup,
    closePaymentPopup,
    getLastPurchaseError,
  } = useUpsalePurchase();

  const [loading, setLoading] = useState(false);
  // Which of the four states the failed-payment sheet shows; undefined = hidden.
  const [paymentErrorState, setPaymentErrorState] = useState<PaymentErrorState | undefined>(
    undefined
  );
  // How the last attempt was paid — the saved method, or a card just typed in
  // the payment window. Keeps the sheet's own events honest.
  const lastMethodRef = useRef<'oneclick' | 'card'>('oneclick');
  // Provider error behind the sheet: the Stripe charge is dispatched from here,
  // so the hook has nothing to report and the retry/close events would lose the
  // decline code without it.
  const lastErrorRef = useRef<any>(null);

  const failedPayment = !!paymentErrorState;

  // Both the charge and every line of copy come from remote config
  // (upsaleDynamic.*), exactly like the shared upsale-dynamic page — this screen
  // only draws the offer. Nothing here is translated in the app bundle, so the
  // mutator alone decides what the user reads and pays.
  //
  // Field → element (top to bottom):
  //   subTitle           → muted line above the discount ("One-time offer")
  //   discountPercent    → the big "NN% OFF" (number, default 50);
  //                        discountLabel (localized, e.g. "50% RABATT") wins when set
  //   title              → title of the gradient offer card
  //   descriptionText    → description at the bottom of the offer card
  //   descriptionTitle   → heading of the "What you get" block
  //   whatYouGetText     → its lines (array of strings per locale)
  //   priceAmount        → the charged price       | sticky block, shown only
  //   oldPriceAmount     → struck-through price    | while showPrices !== false
  //   oneTimePaymentText → "One time payment" line, shown while
  //                        showOneTimePayment !== false
  //   buttonText         → CTA
  //
  // bottomText is deliberately NOT read: the shared default in packages/core
  // is the DNA-report note with its own price, and the hairstyle mutator no
  // longer carries the field — the charged amount is shown in the price row.
  const priceAmount = parsePriceAmount(upsaleDynamic?.priceAmount);
  const priceText = priceAmount ? formatUsd(priceAmount) : '';
  const oldPriceAmount = parsePriceAmount(upsaleDynamic?.oldPriceAmount);
  const upsaleName = upsaleDynamic?.upsaleName || 'hairstyle-ai-hair-report';

  const discountPercent = Number(upsaleDynamic?.discountPercent);
  const discountLabel =
    getLocalizedText(upsaleDynamic?.discountLabel, locale) ||
    `${Number.isFinite(discountPercent) && discountPercent > 0 ? discountPercent : DEFAULTS.discountPercent}% OFF`;
  const offerBadge = getLocalizedText(upsaleDynamic?.subTitle, locale) || DEFAULTS.offerBadgeText;

  const title = getLocalizedText(upsaleDynamic?.title, locale);
  const subTitle = getLocalizedText(upsaleDynamic?.descriptionText, locale);
  const whatYouGetTitle = getLocalizedText(upsaleDynamic?.descriptionTitle, locale);
  const whatYouGetFromConfig = getLocalizedList(upsaleDynamic?.whatYouGetText, locale);
  const whatYouGetAll = whatYouGetFromConfig.length ? whatYouGetFromConfig : DEFAULTS.whatYouGet;
  const buttonText = getLocalizedText(upsaleDynamic?.buttonText, locale) || DEFAULTS.buttonText;
  const oneTimePaymentText =
    getLocalizedText(upsaleDynamic?.oneTimePaymentText, locale) || DEFAULTS.oneTimePaymentText;

  const showPrices = isOn(upsaleDynamic?.showPrices) && !!priceText;
  const showOneTimePayment = isOn(upsaleDynamic?.showOneTimePayment);

  // The price line in "What you get" ("Just {price} instead of {oldPrice}") is
  // the alternative to the sticky block's price row: while showPrices is on,
  // the deal is already stated there and the line is dropped.
  const hasPricePlaceholder = (text: string) => /\{price\}|\{oldPrice\}/.test(text);
  const whatYouGetItems = showPrices
    ? whatYouGetAll.filter((item) => !hasPricePlaceholder(item))
    : whatYouGetAll;

  // A "What you get" line may quote the deal: "Just {price} instead of
  // {oldPrice}". {price} becomes the charged amount and {oldPrice} the
  // struck-through old one, so the copy can never name a price the user is
  // not charged. Function replacers: "$9.99" in a plain replacement string is
  // read as a capture-group reference.
  const oldPriceText = oldPriceAmount ? formatUsd(oldPriceAmount) : '';
  const renderItem = (text: string): React.ReactNode => {
    const withPrice = text.replace(/\{price\}/g, () => priceText);
    const parts = withPrice.split('{oldPrice}');

    if (parts.length === 1) {
      return withPrice;
    }

    return parts.map((part, i) => (
      <React.Fragment key={i}>
        {i > 0 && <s className={styles.strike}>{oldPriceText}</s>}
        {part}
      </React.Fragment>
    ));
  };

  // The offer as the payment layer sees it. The charge is amount-driven, so an
  // id is only needed to tie the payment back to a product in Stripe.
  const stripeProduct = useMemo(() => {
    const currency = (purchaseState?.currency || 'USD').toUpperCase();
    const id =
      upsaleDynamic?.stripeId || upsaleDynamic?.productId || upsaleDynamic?.recurlyId || 'upsale';

    return {
      id,
      productCode: upsaleDynamic?.productId || upsaleDynamic?.stripeId || id,
      amount: priceAmount || 0,
      currency,
      isOneTimePurchase: true,
      title: upsaleDynamic?.title,
      priceText,
      // Shown in the Stripe dashboard and on the receipt.
      description: title || upsaleName,
    };
  }, [upsaleDynamic, priceAmount, priceText, title, upsaleName, purchaseState?.currency]);

  // Offer photo: the user's own selfie (web onboarding / hairstyle try-on),
  // otherwise the designer's placeholder for the gender track.
  const offerPhoto = front || img(trackFromGender(gender), 'upsale-report');

  useEffect(() => {
    Analytics.trackEvent(`step${currentIndex + 1}`, 'view', { page: 'hairstyle-upsale' });
  }, []);

  const errorContext = () => ({
    method: lastMethodRef.current,
    place: 'upsale' as const,
    paymentSystem,
  });

  const errorEventParams = () =>
    getPaymentErrorAnalyticsParams(lastErrorRef.current, errorContext());

  /**
   * Charge the offer once on Stripe: the customer's saved method, or the one
   * just collected in the payment window. A 200 with a non-succeeded intent is
   * still a failure — the reason travels in the body, not as an HTTP error.
   */
  const chargeStripe = async (paymentMethodId?: string) => {
    const result: any = await dispatch(
      createOneClickPayment({
        amount: stripeProduct.amount,
        currency: stripeProduct.currency,
        productCode: stripeProduct.productCode,
        productId: stripeProduct.id,
        description: stripeProduct.description,
        trigger: 'hairstyle-upsale',
        // Retry with another card: charge the freshly collected method and keep
        // it on file — otherwise the backend reuses the one just declined.
        ...(paymentMethodId
          ? { paymentMethodId, setupFutureUsage: 'off_session' as const }
          : {}),
      })
    );

    const clientSecret = result?.client_secret || result?.clientSecret;
    let status = result?.status || result?.payment_intent_status;

    if (clientSecret && result?.requires_action) {
      // Throws the Stripe error (code + decline_code) if the bank refuses during
      // 3-D Secure, which the popup then explains.
      await ensureStripeKeys();
      const paymentIntent = await confirmStripePaymentIntent(clientSecret);
      status = paymentIntent?.status || status;
    }

    if (status && status !== 'succeeded') {
      throw Object.assign(new Error('Hairstyle upsale payment was not completed'), {
        code: result?.error?.code || result?.last_payment_error?.code || status,
        decline_code: extractDeclineCode(result, PaymentSystem.STRIPE),
      });
    }
  };

  /** One purchase: the CTA, or a retry with a card typed in the payment window. */
  const purchase = async (paymentMethodId?: string, tokenId?: string) => {
    setLoading(true);
    setPaymentErrorState(undefined);
    lastMethodRef.current = paymentMethodId || tokenId ? 'card' : 'oneclick';

    try {
      if (isStripe) {
        await chargeStripe(paymentMethodId);
      } else {
        const data = await handleUpsalePurchase({
          recurlyId: upsaleDynamic?.recurlyId,
          upsaleName,
          isNextStepBlocked: true,
          tokenId,
          // The popup explains the failure — a toast on top of it only repeats.
          noToast: true,
        });

        // This branch resolves with `undefined` instead of throwing, so the
        // provider error is read from the hook — without it the popup could
        // only ever fall back to state 3 ("try again").
        if (!data) {
          throw getLastPurchaseError() || new Error('Hairstyle upsale purchase failed');
        }
      }

      Analytics.trackEvent('hairstyleUpsale', 'success', { upsaleID: upsaleName });
      onNext();
    } catch (error) {
      lastErrorRef.current = error;

      Analytics.trackEvent('hairstyleUpsale', 'error', {
        ...getPaymentErrorAnalyticsParams(error, errorContext()),
        upsaleID: upsaleName,
      });

      // showUpsalePaymentError=false (remote): no popup, the offer is simply
      // dropped and the user moves on — same contract as upsale-dynamic.
      if (showUpsalePaymentError === false) {
        onNext();
        return;
      }

      dispatch(handleErrorPurchase(error, { noToast: true }));
      setPaymentErrorState(resolvePaymentErrorState(error, errorContext()));
      Analytics.trackEvent(SHEET_CATEGORY, 'open', errorEventParams());
    } finally {
      setLoading(false);
    }
  };

  /**
   * Popup button. State 3 is a glitch on the way to the bank, so it just repeats
   * the charge on the saved method; the other states need a different card or a
   * bank confirmation, which only the payment window can offer.
   */
  const handleErrorPopupSubmit = async () => {
    Analytics.trackEvent(SHEET_CATEGORY, 'retry', errorEventParams());
    const state = paymentErrorState;
    setPaymentErrorState(undefined);

    if (state === PaymentErrorState.TEMPORARY) {
      purchase();
      return;
    }

    // The card form is Stripe.js — after a reload it has no key yet.
    if (isStripe) {
      await ensureStripeKeys();
    }

    showPaymentPopup();
  };

  /** Cross / dim tap: back to the offer, CTA live again. Does not move the flow on. */
  const handleErrorPopupClose = () => {
    Analytics.trackEvent(SHEET_CATEGORY, 'close', errorEventParams());
    setPaymentErrorState(undefined);
  };

  /** A payment method was tokenized in the window — charge the offer with it. */
  const handleCardCollected = async (formData: any) => {
    const paymentMethodId = formData?.payment_method_id;
    const tokenId = formData?.token_id;

    // No fresh method (e.g. a PayPal submit): retrying would charge the very
    // card that has just been declined, so let the window finish its own flow.
    if (!paymentMethodId && !tokenId) {
      return;
    }

    closePaymentPopup();
    await purchase(paymentMethodId, tokenId);
  };

  const handleSkip = () => {
    Analytics.trackEvent('hairstyleUpsale', 'skip', { upsaleID: upsaleName });
    onNext();
  };

  return (
    <div className={styles.page}>
      <button type="button" className={styles.close} onClick={handleSkip} aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      {/* "One-time offer" + the big gradient discount */}
      <div className={styles.offerTop}>
        <span className={styles.offerLabel}>{offerBadge}</span>
        <h1 className={styles.discount}>{discountLabel}</h1>
      </div>

      {/* Offer art: tilted gradient card with the title in front, the user's
          selfie (or the placeholder) behind it on the right */}
      <div className={styles.cards}>
        {offerPhoto && (
          <div className={styles.photoCard}>
            <img src={offerPhoto} alt="" />
          </div>
        )}
        <div className={styles.offerCard}>
          <span className={styles.brand}>HAIRIO</span>
          <p className={styles.offerTitle}>{title}</p>
          <p className={styles.offerDesc}>{subTitle}</p>
        </div>
      </div>

      <section className={styles.getCard}>
        {whatYouGetTitle && <h2 className={styles.getTitle}>{whatYouGetTitle}</h2>}
        <ul className={styles.getList}>
          {whatYouGetItems.map((item) => (
            <li key={item} className={styles.getItem}>
              <span className={styles.check}>
                <CheckIcon />
              </span>
              <span>{renderItem(item)}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Sticky block: pinned to the bottom of the viewport, the content above
          scrolls underneath its gradient. Every optional line is simply not
          rendered when switched off, so the block collapses without a gap. */}
      <div className={styles.sticky}>
        {showPrices && (
          <div className={styles.priceRow}>
            {oldPriceAmount && (
              <>
                <span className={styles.oldPrice}>{formatUsd(oldPriceAmount)}</span>
                <span className={styles.priceArrow} aria-hidden="true">→</span>
              </>
            )}
            <span className={styles.price}>{priceText}</span>
          </div>
        )}

        {showOneTimePayment && <p className={styles.oneTime}>{oneTimePaymentText}</p>}

        <button
          type="button"
          className={styles.cta}
          onClick={() => purchase()}
          disabled={loading}
        >
          {buttonText}
        </button>

        {/* The legal pages live in the funnel itself, so they open in place
            instead of leaving for hairio.app and losing the offer. Own keys:
            personalPlan.plan.* are inflected for a sentence ("...bound by the
            Privacy Policy") and read wrong as standalone links in cs/fr/it. */}
        <div className={styles.links}>
          <a onClick={() => setPage('privacy-policy')}>{t('hairstyleUpsale.privacy')}</a>
          <a onClick={() => setPage('terms-of-use')}>{t('hairstyleUpsale.terms')}</a>
        </div>
      </div>

      {/* The window only collects a payment method: it hands the method back and
          the charge happens here, on the screen that knows the product. For
          Stripe it must price the offer itself, not the paywall product the
          funnel config points at. */}
      {renderPaymentProcessor({
        activeProduct: isStripe ? stripeProduct : upsaleDynamic?.product,
        oneTimeRecurlyId: upsaleDynamic?.recurlyId,
        // A declined charge often needs another method entirely, wallets
        // included, so the window offers all of them.
        isOnlyCard: false,
        onCardCollected: handleCardCollected,
      })}

      {failedPayment && showUpsalePaymentError !== false && (
        <ErrorPopup
          visible
          errorState={paymentErrorState}
          onClose={handleErrorPopupClose}
          onSubmit={handleErrorPopupSubmit}
        />
      )}
    </div>
  );
}

export default Page;
